import React, { useState, useEffect, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Container,
  Card,
  CardBody,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Input,
  FormGroup,
  Label,
  Alert,
  Spinner,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import MetaTags from "react-meta-tags";
import logo from "../../assets/images/logo/glogix-logo-white.png";

const inputStyle = {
  backgroundColor: "rgba(11, 17, 32, 0.9)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  color: "#fff",
};

// The portal's dark theme makes the default outline buttons almost invisible, so give them a clear colour
const visibleButtonStyle = { color: "#e2e8f0", borderColor: "rgba(226, 232, 240, 0.45)" };

const STATUS_COLORS = { sent: "success", queued: "info", sending: "info", batched: "secondary", failed: "danger" };

function formatDateTime(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

/**
 * GuardLogix admin portal: every client, the email its alerts go to, and a way to change it.
 */
const ClientsManagement = () => {
  const history = useHistory();
  const adminToken = localStorage.getItem("adminToken");
  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");

  const [data, setData] = useState({ clients: [], emailAlertsEnabled: false, testRedirect: false, maxAlertEmails: 5 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [alertInfo, setAlertInfo] = useState({ show: false, type: "", message: "" });

  const [edit, setEdit] = useState({ open: false, client: null, emailsText: "", enabled: true, saving: false, error: "", testing: false, info: "" });
  const [testingOrg, setTestingOrg] = useState(null);
  const [historyModal, setHistoryModal] = useState({ open: false, client: null, loading: false, rows: [] });

  const apiBase = () => {
    const base = process.env.REACT_APP_DEFAULTPATH || "http://localhost:1337/";
    return base.endsWith("/") ? base : `${base}/`;
  };

  const showAlert = (type, message, timeout = 7000) => {
    setAlertInfo({ show: true, type, message });
    if (timeout) setTimeout(() => setAlertInfo({ show: false, type: "", message: "" }), timeout);
  };

  const leave = useCallback(() => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    history.push("/admin-login");
  }, [history]);

  // Calls the admin API; sends the admin back to the login page when the session is no longer valid
  const callApi = useCallback(
    async (path, options = {}) => {
      const res = await fetch(`${apiBase()}api/admin/clients${path}`, {
        ...options,
        headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json", ...(options.headers || {}) },
      });
      if (res.status === 401 || res.status === 403) {
        leave();
        throw new Error("Your admin session has ended. Please log in again.");
      }
      const body = await res.json().catch(() => ({}));
      return { ok: res.ok, status: res.status, body };
    },
    [adminToken, leave]
  );

  const load = useCallback(async () => {
    if (!adminToken) {
      history.push("/admin-login");
      return;
    }
    setLoading(true);
    try {
      const { ok, body } = await callApi("");
      if (ok && body.status) setData(body);
      else showAlert("danger", body.error || "Could not load clients.");
    } catch (e) {
      showAlert("danger", e.message);
    } finally {
      setLoading(false);
    }
  }, [adminToken, callApi, history]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (client) =>
    setEdit({ open: true, client, emailsText: (client.alertEmails || []).join(", "), enabled: client.emailEnabled, saving: false, error: "", testing: false, info: "" });

  // Sends a test email to the address(es) typed in the popup, without saving them
  const sendTestFromModal = async () => {
    setEdit((s) => ({ ...s, testing: true, error: "", info: "" }));
    try {
      const typed = edit.emailsText.trim();
      const { ok, body } = await callApi(`/${encodeURIComponent(edit.client.orgId)}/test-email`, {
        method: "POST",
        body: JSON.stringify(typed ? { to: typed } : {}),
      });
      if (ok) {
        const note = body.redirected
          ? `Test mode is on: the test email meant for ${body.intendedFor.join(", ")} was sent to ${body.sentTo.join(", ")} instead.`
          : `Test email sent to ${body.sentTo.join(", ")}.`;
        setEdit((s) => ({ ...s, testing: false, info: typed ? `${note} It is not saved yet. Click Save to use it for alerts.` : note }));
      } else {
        setEdit((s) => ({ ...s, testing: false, error: body.error || "Test email failed." }));
      }
    } catch (err) {
      setEdit((s) => ({ ...s, testing: false, error: err.message }));
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEdit((s) => ({ ...s, saving: true, error: "" }));
    try {
      const { ok, body } = await callApi(`/${encodeURIComponent(edit.client.orgId)}/alert-settings`, {
        method: "PUT",
        body: JSON.stringify({ alertEmails: edit.emailsText, emailEnabled: edit.enabled }),
      });
      if (!ok) {
        setEdit((s) => ({ ...s, saving: false, error: body.error || "Could not save." }));
        return;
      }
      setEdit({ open: false, client: null, emailsText: "", enabled: true, saving: false, error: "" });
      showAlert("success", `Alert email for ${edit.client.orgName || edit.client.orgId} saved. Alerts will go to ${body.effectiveEmails.join(", ") || "nobody (switched off)"}.`);
      load();
    } catch (err) {
      setEdit((s) => ({ ...s, saving: false, error: err.message }));
    }
  };

  const sendTest = async (client) => {
    setTestingOrg(client.orgId);
    try {
      const { ok, body } = await callApi(`/${encodeURIComponent(client.orgId)}/test-email`, { method: "POST", body: JSON.stringify({}) });
      if (ok) {
        showAlert(
          "success",
          body.redirected
            ? `Test mode is on: the test email for ${body.intendedFor.join(", ")} was sent to ${body.sentTo.join(", ")} instead.`
            : `Test email sent to ${body.sentTo.join(", ")}.`
        );
      } else {
        showAlert("danger", body.error || "Test email failed.");
      }
    } catch (err) {
      showAlert("danger", err.message);
    } finally {
      setTestingOrg(null);
    }
  };

  const openHistory = async (client) => {
    setHistoryModal({ open: true, client, loading: true, rows: [] });
    try {
      const { ok, body } = await callApi(`/${encodeURIComponent(client.orgId)}/emails`);
      setHistoryModal({ open: true, client, loading: false, rows: ok ? body.emails || [] : [] });
    } catch (err) {
      setHistoryModal({ open: true, client, loading: false, rows: [] });
      showAlert("danger", err.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    history.push("/login");
  };

  const q = search.trim().toLowerCase();
  const visible = data.clients.filter(
    (c) =>
      !q ||
      (c.orgName || "").toLowerCase().includes(q) ||
      c.orgId.toLowerCase().includes(q) ||
      c.loginEmails.some((e) => e.includes(q)) ||
      c.effectiveEmails.some((e) => e.includes(q))
  );

  return (
    <React.Fragment>
      <MetaTags>
        <title>Clients & Alert Emails | Glogix</title>
      </MetaTags>

      <div className="min-vh-100 py-4" style={{ backgroundColor: "#0d131f", color: "#e2e8f0", fontFamily: "inherit" }}>
        <Container fluid="lg">
          <div
            className="d-flex flex-wrap align-items-center justify-content-between p-3 mb-4 rounded-3 shadow"
            style={{ backgroundColor: "rgba(19, 26, 42, 0.9)", border: "1px solid rgba(255, 255, 255, 0.08)" }}
          >
            <div className="d-flex align-items-center gap-3">
              <img src={logo} alt="Glogix" height="34" />
              <div className="border-start border-secondary ps-3 ms-2">
                <h5 className="mb-0 text-white fw-bold d-flex align-items-center gap-2">
                  <FeatherIcon icon="users" size={18} className="text-info" />
                  Clients & Alert Emails
                </h5>
                <span className="text-muted fs-12">Where each client's security alerts are emailed</span>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
              <div className="d-none d-md-block me-2 text-end">
                <div className="text-white fs-13 fw-semibold">{adminUser.name || "Master Administrator"}</div>
                <div className="text-muted fs-11">{adminUser.email || "admin@guardlogix.com"}</div>
              </div>
              <Link to="/admin/api-keys" className="btn btn-outline-light btn-sm d-flex align-items-center gap-1" style={{ borderRadius: "6px", ...visibleButtonStyle }}>
                <FeatherIcon icon="key" size={14} />
                <span className="d-none d-sm-inline">API Keys</span>
              </Link>
              <Button color="danger" size="sm" onClick={handleLogout} className="d-flex align-items-center gap-1" style={{ borderRadius: "6px" }}>
                <FeatherIcon icon="log-out" size={14} />
                <span className="d-none d-sm-inline">Exit Portal</span>
              </Button>
            </div>
          </div>

          {alertInfo.show && (
            <Alert color={alertInfo.type} className="d-flex align-items-center justify-content-between shadow-sm mb-3" style={{ borderRadius: "8px" }}>
              <span className="fs-13 fw-medium">{alertInfo.message}</span>
              <Button close onClick={() => setAlertInfo({ show: false, type: "", message: "" })} className="p-0 fs-16" />
            </Alert>
          )}
          {!loading && !data.emailAlertsEnabled && (
            <Alert color="warning" className="mb-3 fs-13" style={{ borderRadius: "8px" }}>
              Email alerts are switched <b>off</b> on the server (<code>EMAIL_ALERTS_ENABLED</code>). You can set addresses and send test emails, but no alert emails go out until it is turned on.
            </Alert>
          )}
          {!loading && data.testRedirect && (
            <Alert color="info" className="mb-3 fs-13" style={{ borderRadius: "8px" }}>
              Test mode is on (<code>TEST_EMAIL_OVERRIDE</code>): every email goes to the test address, not to the clients.
            </Alert>
          )}

          <Card className="border-0 shadow-sm" style={{ backgroundColor: "#161f30", borderRadius: "10px" }}>
            <CardBody className="p-0">
              <div className="p-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div className="text-white fw-semibold">
                  {data.clients.length} client{data.clients.length === 1 ? "" : "s"}
                </div>
                <Input
                  type="search"
                  placeholder="Search client, id or email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ ...inputStyle, maxWidth: "320px" }}
                />
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="info" />
                </div>
              ) : visible.length === 0 ? (
                <div className="text-center text-muted py-5 fs-13">No clients found.</div>
              ) : (
                <div className="table-responsive">
                  <Table className="table align-middle mb-0 table-hover table-dark">
                    <thead style={{ backgroundColor: "#111827", textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px" }}>
                      <tr>
                        <th className="ps-4">Client</th>
                        <th>Login email</th>
                        <th>Alerts go to</th>
                        <th>Last email</th>
                        <th className="text-end pe-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((c) => (
                        <tr key={c.orgId} style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.05)" }}>
                          <td className="ps-4">
                            <div className="fw-semibold text-white">{c.orgName || "Unnamed client"}</div>
                            <div className="text-muted fs-11">ID {c.orgId} · {c.userCount} user{c.userCount === 1 ? "" : "s"}</div>
                          </td>
                          <td className="fs-13">{c.loginEmails.join(", ")}</td>
                          <td className="fs-13">
                            {c.source === "disabled" ? (
                              <Badge color="warning" pill>Alerts off</Badge>
                            ) : c.effectiveEmails.length ? (
                              <>
                                <div>{c.effectiveEmails.join(", ")}</div>
                                <Badge color={c.source === "custom" ? "info" : "secondary"} pill className="mt-1">
                                  {c.source === "custom" ? "Custom" : "Default (login email)"}
                                </Badge>
                              </>
                            ) : (
                              <span className="text-muted">No email</span>
                            )}
                          </td>
                          <td className="fs-12">
                            {c.lastEmail ? (
                              <span title={c.lastEmail.error || ""}>
                                <Badge color={STATUS_COLORS[c.lastEmail.status] || "secondary"} pill>{c.lastEmail.status}</Badge>
                                <span className="text-muted ms-2">{formatDateTime(c.lastEmail.at)}</span>
                              </span>
                            ) : (
                              <span className="text-muted">None yet</span>
                            )}
                          </td>
                          <td className="text-end pe-4 text-nowrap">
                            <Button color="outline-info" size="sm" className="me-2" onClick={() => openEdit(c)}>
                              <FeatherIcon icon="edit-2" size={13} /> Edit
                            </Button>
                            <Button color="outline-light" size="sm" className="me-2" style={visibleButtonStyle} disabled={testingOrg === c.orgId || !c.effectiveEmails.length} onClick={() => sendTest(c)}>
                              {testingOrg === c.orgId ? <Spinner size="sm" /> : <FeatherIcon icon="send" size={13} />} Test
                            </Button>
                            <Button color="outline-secondary" size="sm" style={visibleButtonStyle} onClick={() => openHistory(c)}>
                              <FeatherIcon icon="clock" size={13} /> History
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Edit alert email */}
      <Modal
        isOpen={edit.open}
        toggle={() => !edit.saving && setEdit((s) => ({ ...s, open: false }))}
        centered
        style={{ color: "#fff" }}
        contentClassName="bg-dark border border-secondary border-opacity-50"
      >
        <ModalHeader toggle={() => !edit.saving && setEdit((s) => ({ ...s, open: false }))} className="border-bottom border-secondary border-opacity-25">
          <span className="fw-bold">Alert email: {edit.client ? edit.client.orgName || edit.client.orgId : ""}</span>
        </ModalHeader>
        <form onSubmit={saveEdit}>
          <ModalBody className="p-4">
            {edit.error && <Alert color="danger" className="fs-13">{edit.error}</Alert>}
            {edit.info && <Alert color="success" className="fs-13">{edit.info}</Alert>}
            <FormGroup className="mb-3">
              <Label className="fs-13 fw-semibold text-light">Send this client's alerts to</Label>
              <Input
                type="textarea"
                rows={2}
                value={edit.emailsText}
                onChange={(e) => setEdit((s) => ({ ...s, emailsText: e.target.value }))}
                placeholder={edit.client ? `Leave empty to use the login email (${edit.client.loginEmails.join(", ")})` : ""}
                style={inputStyle}
              />
              <div className="text-muted fs-12 mt-1">
                One or more addresses separated by commas, up to {data.maxAlertEmails}. Clients cannot change this themselves.
              </div>
            </FormGroup>
            <FormGroup check className="mb-1">
              <Input
                type="checkbox"
                id="clientEmailEnabled"
                checked={edit.enabled}
                onChange={(e) => setEdit((s) => ({ ...s, enabled: e.target.checked }))}
              />
              <Label check for="clientEmailEnabled" className="fs-13 text-light">
                Send alert emails to this client
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter className="border-top border-secondary border-opacity-25 justify-content-between">
            <Button color="info" type="button" outline disabled={edit.saving || edit.testing} onClick={sendTestFromModal}>
              {edit.testing ? <Spinner size="sm" /> : <FeatherIcon icon="send" size={13} />} Send test email
            </Button>
            <div className="d-flex gap-2">
              <Button color="secondary" type="button" outline disabled={edit.saving} onClick={() => setEdit((s) => ({ ...s, open: false }))} style={visibleButtonStyle}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={edit.saving || edit.testing}>
                {edit.saving ? <Spinner size="sm" /> : "Save"}
              </Button>
            </div>
          </ModalFooter>
        </form>
      </Modal>

      {/* Recent emails */}
      <Modal
        isOpen={historyModal.open}
        toggle={() => setHistoryModal((s) => ({ ...s, open: false }))}
        centered
        size="lg"
        style={{ color: "#fff" }}
        contentClassName="bg-dark border border-secondary border-opacity-50"
      >
        <ModalHeader toggle={() => setHistoryModal((s) => ({ ...s, open: false }))} className="border-bottom border-secondary border-opacity-25">
          <span className="fw-bold">Recent emails: {historyModal.client ? historyModal.client.orgName || historyModal.client.orgId : ""}</span>
        </ModalHeader>
        <ModalBody className="p-0">
          {historyModal.loading ? (
            <div className="text-center py-4"><Spinner color="info" /></div>
          ) : historyModal.rows.length === 0 ? (
            <div className="text-center text-muted py-4 fs-13">No alert emails yet.</div>
          ) : (
            <div className="table-responsive">
              <Table className="table align-middle mb-0 table-dark fs-12">
                <thead style={{ backgroundColor: "#111827", textTransform: "uppercase", fontSize: "11px" }}>
                  <tr><th className="ps-3">Subject</th><th>Sent to</th><th>Status</th><th>When</th></tr>
                </thead>
                <tbody>
                  {historyModal.rows.map((r) => (
                    <tr key={r._id}>
                      <td className="ps-3">{r.subject}</td>
                      <td>{(r.to || []).join(", ")}</td>
                      <td>
                        <Badge color={STATUS_COLORS[r.status] || "secondary"} pill title={r.lastError || ""}>{r.status}</Badge>
                        {r.status === "failed" && r.lastError && <div className="text-danger fs-11">{r.lastError}</div>}
                      </td>
                      <td className="text-nowrap">{formatDateTime(r.sentAt || r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ClientsManagement;
